import {db} from "@/main";
import {
    FirestoreDataConverter,
    collection,
    query,
    where,
    getDocs,
    setDoc,
    doc,
    deleteDoc,
    writeBatch, getDoc
} from "firebase/firestore";
import { toAppTimezone } from '@/utils/dateUtils';

const wishDataConverter: FirestoreDataConverter<WishData> = {
    toFirestore(data: WishData) {
        return data;
    },
    fromFirestore(snapshot) {
        const data = snapshot.data();
        return { ...data, } as WishData;
    }
};

const wishDataDateConverter: FirestoreDataConverter<WishDataDate> = {
    toFirestore(data: WishDataDate) {
        return data;
    },
    fromFirestore(snapshot) {
        const data = snapshot.data();
        return { 
            ...data,
            date: data.date.toDate(),
        } as WishDataDate;
    }
};

export function GetWishData(wishPeriodId: string, userId:string): Promise<WishData> {
    return new Promise<WishData>(async (resolve, reject) => {
        try {
            const wishDataRef = collection(db, "wishData").withConverter(wishDataConverter);
            const wishDataQuery = query(
                wishDataRef,
                where("employNr", "==", userId),
                where("wishPeriodId", "==", wishPeriodId)
            );

            const wishDataSnapshot = await getDocs(wishDataQuery);
            
            if (!wishDataSnapshot.empty) {
                const returnWishData = {...wishDataSnapshot.docs[0].data(), dates: []} as WishData;
                wishDataSnapshot.docs[0].data().dates = [];

                const wishDataDatesColRef = collection(wishDataSnapshot.docs[0].ref, "dates").withConverter(wishDataDateConverter);
                getDocs(wishDataDatesColRef)
                    .then((wishDataDateSnapshot) => {
                        wishDataDateSnapshot.forEach((doc) => {
                            returnWishData.dates.push(doc.data() as WishDataDate);
                        });
                    })
                    .catch((error) => {
                        reject(`Error fetching WishDataDates: ${error}`);
                    }).finally(() => {
                    resolve(returnWishData);
                });
            } else {
                reject("No matching wishData found!");
            }
        } catch (error) {
            reject(error);
        }
})}

export function GetWishesData(userId:string | undefined): Promise<WishData[]> {
    return new Promise<WishData[]>(async (resolve, reject) => {
        try {
            const wishDataRef = collection(db, "wishData").withConverter(wishDataConverter);

            const wishDataQuery = query(
                wishDataRef,
                where("employNr", "==", userId)
//                where("userId", "==", userId)
            );

            const wishDataSnapshot = await getDocs(wishDataQuery);

            if (!wishDataSnapshot.empty) {
                
                const returnWishData: WishData[] = [];
                
                for (const wishData of wishDataSnapshot.docs) {
                    returnWishData.push({...wishData.data(), dates: []} as WishData);
                }
                
                resolve(returnWishData);

            } else {
                reject("No matching wishData found!");
            }
        } catch (error) {
            reject(error);
        }
    })}

export function InsertWishData(wishData: WishData, isFinished:boolean): Promise<void> {
    return new Promise<void>((resolve, reject) => {
        setDoc(doc(db, "wishData", wishData.id), {   
                id: wishData.id,
                wishPeriodId: wishData.wishPeriodId.toString(),
                userId: wishData.userId.toString(),
                employNr: wishData.employNr !== null && wishData.employNr !== undefined ? wishData.employNr : '',
                bemerkung: wishData.bemerkung,
                finished: isFinished,
        }, { merge: true })
            .then(() => {
                resolve();
            })
            .catch((error) => {
                reject(error);
            });
    
})}

export function InsertWishDataDates(wishDataId: string, wishPeriodId: string, employNr:string | null | undefined, wishDataDates: WishDataDate[]): Promise<void> {
    return new Promise<void>(async (resolve, reject) => {
        try {
            let wishDataDoc = doc(db, "wishData", wishDataId);
            
            const dateColRef = collection(wishDataDoc, "dates");
            
            const dateSnapshot = await getDocs(dateColRef);

            let batch = writeBatch(db);
            let batchCount = 0;

            for (const document of dateSnapshot.docs) {
                batch.delete(doc(db, "wishData", wishDataId, "dates", document.id));
                batchCount++;

                if (batchCount === 500) {
                    await batch.commit();
                    batch = writeBatch(db);
                    batchCount = 0;
                }
            }

            if (batchCount > 0) {
                await batch.commit();
            }

            batch = writeBatch(db);
            batchCount = 0;
            
            for (const date of wishDataDates) {
                // Datum in App-Zeitzone konvertieren für konsistente Speicherung
                const appTimezoneDate = toAppTimezone(date.date);
                
                batch.set(doc(db, "wishData", wishDataId, "dates", date.id), {
                    id: date.id,
                    date: appTimezoneDate,
                    shift: date.shift,
                    wishPeriodId: wishPeriodId,
                    employNr: employNr !== null && employNr !== undefined ? employNr : '',
                });
                batchCount++;

                if (batchCount === 500) {
                    await batch.commit();
                    batch = writeBatch(db);
                    batchCount = 0;
                }
            }
            
            if (batchCount > 0) {
                await batch.commit();
            }
        } catch (error) {
            reject(error);
        }
        finally{
            resolve();
        }
    });
}